import { FormContext } from "@common/contexts/Form";
import FormField, { type FormFieldProps } from "@common/controls/Form/Field/component";
import Icon from "@common/controls/Icon/component";
import { SelectWithAdd } from "@common/controls/Inputs/Select/component";
import Menu, { type MenuContentProps } from "@common/controls/Menu/component";
import DeleteModal from "@common/controls/Modal/Modals/Delete/component";
import { IconType, type InputTypeProps, type OptionItem } from "@common/declarations";
import { ContentAlign } from "@common/hocs/withDropdown/lib";
import { useI18n } from "@common/hooks/i18n";
import { removeItem, updateItemBy } from "@common/lib/array";
import { useAsyncSource, useVisibleModal } from "@common/lib/hooks";
import { EMPTY_OBJECT } from "@common/lib/misc";
import { withSavedToast } from "@common/lib/toast";
import { type Account } from "@common/modules/account/types";
import React from "react";
import { AddEditRecipientModal } from "./Recipient/AddUpdateModal";
import RecipientPreview from "./Recipient/Preview/component";
import { type TransferApi } from "./types";

type AddCreateRecipientFormFieldProps = Omit<
  FormFieldProps<string | null | undefined, InputTypeProps<string>>,
  "Component" | "componentOpts"
> & {
  api: TransferApi;
  placeholder?: string;
  recipientStyle?: React.CSSProperties;
  selectAlign?: ContentAlign;
  selectStyle?: React.CSSProperties;
};

type ModalData = {
  item: Account.View.Beneficiary | null;
  type: "delete" | "add" | "update";
};
const AddCreateRecipientFormField = ({
  api,
  placeholder,
  recipientStyle,
  selectAlign,
  selectStyle,
  ...fieldProps
}: AddCreateRecipientFormFieldProps) => {
  const i18n = useI18n();

  const [visibleModal, setVisibleModal, hideModal] = useVisibleModal<ModalData>();

  const [recipients, setRecipients, isLoading] = useAsyncSource(api.recipient.get, []);

  const formContext = React.useContext(FormContext);

  const onAddNew = React.useCallback(
    (data: Account.View.Beneficiary) =>
      withSavedToast(async () => {
        const recipient = await api.recipient.add(data);
        setRecipients((x) => [...x, recipient]);
        hideModal();

        const onChange = fieldProps.onChange ?? formContext.onChange;
        onChange(fieldProps.name, recipient.beneficiaryId);
      }),
    [fieldProps.name, fieldProps.onChange, formContext.onChange]
  );

  const onDelete = React.useCallback(
    () =>
      withSavedToast(async () => {
        const beneficiaryId = visibleModal!.item!.beneficiaryId;
        await api.recipient.remove!(beneficiaryId);
        setRecipients((x) => removeItem(x, (y) => y.beneficiaryId === beneficiaryId));
        hideModal();
      }),
    [visibleModal?.item]
  );

  const onUpdate = React.useCallback(
    (data: Account.View.Beneficiary) =>
      withSavedToast(async () => {
        const recipient = await api.recipient.update(data);
        setRecipients((x) =>
          updateItemBy(
            x,
            (y) => y.beneficiaryId === data.beneficiaryId,
            (y) => recipient
          )
        );
        hideModal();
      }),
    []
  );

  const itemMenu = React.useCallback(
    (props: MenuContentProps<OptionItem<any>>) => (
      <>
        <div
          className="action"
          onClick={() =>
            setVisibleModal({ item: recipients!.find((x) => x.beneficiaryId === props.item.value)!, type: "update" })
          }
        >
          <Icon icon={IconType.Cog} />
          {i18n.update}
        </div>
        {!!api.recipient.remove && (
          <div
            className="action"
            onClick={() =>
              setVisibleModal({ item: recipients!.find((x) => x.beneficiaryId === props.item.value)!, type: "delete" })
            }
          >
            <Icon icon={IconType.Delete} />
            {i18n.delete}
          </div>
        )}
      </>
    ),
    [i18n, onDelete, onUpdate, recipients]
  );

  const options = React.useMemo(
    () =>
      (recipients ?? []).map((x) => ({
        value: x.beneficiaryId,
        label: x.name,
        descriptions:
          x.data.wise && x.bankName
            ? [{ label: x.bankName, isHint: true }]
            : x.data.wallet && x.currencyCode
            ? [{ label: x.currencyCode, isHint: true }]
            : undefined
      })),
    [recipients]
  );

  const recipient = React.useMemo(
    () => recipients?.find((x) => x.beneficiaryId === fieldProps.value),
    [recipients, fieldProps.value]
  );

  return (
    <>
      <FormField
        {...fieldProps}
        Component={SelectWithAdd}
        componentProps={{
          isLoading,
          addTitle: i18n.newRecipient,
          align: selectAlign,
          onAddClick: () => setVisibleModal({ item: null, type: "add" }),
          options,
          placeholder,
          renderItemAddon: (x) => (
            <Menu
              dropdownClassName="no-close-click-outside"
              toggleIcon={IconType.EllipsisHorizontal}
              item={x}
              Content={itemMenu}
              align={ContentAlign.Right}
              noArrow
              theme="white"
              name={fieldProps.name + "Menu"}
            />
          ),
          sort: "asc",
          style: selectStyle
        }}
      />

      {!!recipient && (
        <RecipientPreview
          getDynamicFields={api.recipient.getDynamicData}
          lookupAccount={api.lookupAccount}
          recipient={recipient}
          style={recipientStyle}
        />
      )}

      <AddEditRecipientModal
        api={api}
        defaultValues={visibleModal?.item ?? EMPTY_OBJECT}
        isVisible={visibleModal?.type === "add" || visibleModal?.type === "update"}
        onClose={hideModal}
        previewStyle={recipientStyle}
        submit={visibleModal?.type === "add" ? onAddNew : onUpdate}
      />

      <DeleteModal
        isVisible={visibleModal?.type === "delete"}
        title={i18n.deleteRecipient}
        message={i18n.infoDeleteConfirmationRecipient}
        onDelete={onDelete}
        onClose={hideModal}
      />
    </>
  );
};
export default AddCreateRecipientFormField;
